import { Controller } from "@hotwired/stimulus"

// The compose box is a textarea so multi-line fragments paste in intact. It
// grows with its content, sends on Enter (Shift+Enter = new line), and holds a
// pending attachment until it's sent or removed.
export default class extends Controller {
  static targets = ["input", "file", "attachment", "preview", "fileName", "fileSize"]
  static values = { limits: Object }

  static MAX_HEIGHT = 160

  connect() {
    this.autogrow()
  }

  autogrow() {
    if (!this.hasInputTarget) return
    const input = this.inputTarget
    input.style.height = "auto"
    input.style.height = `${Math.min(input.scrollHeight, this.constructor.MAX_HEIGHT)}px`
  }

  submitOnEnter(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return

    event.preventDefault()
    if (!this.hasContent) return
    this.inputTarget.form?.requestSubmit()
  }

  // Text alone or an attachment alone are both sendable; nothing at all is not.
  get hasContent() {
    return this.inputTarget.value.trim().length > 0 || this.hasAttachment
  }

  get hasAttachment() {
    return this.hasFileTarget && this.fileTarget.files.length > 0
  }

  // Pasting an image attaches it directly, instead of save-to-disk + the
  // paperclip. Images only — pasted documents keep their default behaviour.
  paste(event) {
    const items = event.clipboardData?.items
    if (!items) return

    const imageItem = [...items].find((item) => item.kind === "file" && item.type.startsWith("image/"))
    if (!imageItem) return

    const file = imageItem.getAsFile()
    if (!file) return

    event.preventDefault()
    const transfer = new DataTransfer()
    // Clipboard images often arrive as a generic "image.png" — timestamp it so
    // two pastes don't look like the same file.
    transfer.items.add(new File([file], `pegado-${Date.now()}.${(file.type.split("/")[1] || "png")}`, { type: file.type }))
    this.fileTarget.files = transfer.files
    this.fileSelected()
  }

  fileSelected() {
    const file = this.fileTarget.files[0]
    if (!file) return this.clearAttachment()

    const problem = this.validate(file)
    if (problem) {
      window.alert(problem)
      return this.clearAttachment()
    }

    if (this.hasFileNameTarget) this.fileNameTarget.textContent = file.name
    if (this.hasFileSizeTarget) this.fileSizeTarget.textContent = this.humanSize(file.size)
    this.showImagePreview(file)

    this.attachmentTarget.classList.remove("hidden")
    this.attachmentTarget.classList.add("flex")
    this.inputTarget.placeholder = "Agrega un comentario (opcional)..."
    this.inputTarget.focus()
  }

  clearAttachment() {
    if (this.hasFileTarget) this.fileTarget.value = ""
    if (this.hasAttachmentTarget) {
      this.attachmentTarget.classList.add("hidden")
      this.attachmentTarget.classList.remove("flex")
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
      this.previewTarget.removeAttribute("src")
    }
    if (this.hasInputTarget) this.inputTarget.placeholder = "Escribe un mensaje..."
  }

  // Mirrors the server-side limits so an oversized file is caught before the
  // upload starts. An unknown browser MIME type is left to the server.
  validate(file) {
    const limits = this.hasLimitsValue ? this.limitsValue : {}
    const max = limits[file.type]

    if (file.type && Object.keys(limits).length > 0 && max === undefined) {
      return `WhatsApp no acepta este tipo de archivo (${file.type}).`
    }
    if (max !== undefined && file.size > max) {
      return `El archivo es demasiado grande (máximo ${this.humanSize(max)}).`
    }
    return null
  }

  showImagePreview(file) {
    if (!this.hasPreviewTarget) return
    if (!file.type.startsWith("image/")) {
      this.previewTarget.classList.add("hidden")
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      this.previewTarget.src = event.target.result
      this.previewTarget.classList.remove("hidden")
    }
    reader.readAsDataURL(file)
  }

  humanSize(bytes) {
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
  }
}
